@if (isLoading || isUpdating) {
  <div class="overlay">
    <div class="spinner"></div>
    <p class="text-load">&nbsp;&nbsp; Loading...</p>
  </div>
}

<div class="breadcrumb-bar">
  <div class="breadcrumb">
    <a class="breadcrumb-link" [routerLink]="['/action-plan']">Action Plan</a>
    <span class="breadcrumb-sep" aria-hidden="true"></span>
    <span class="breadcrumb-current">AP-{{ actionPlanId }}</span>
  </div>
</div>

<main class="main-content">

  <!-- ─── Review Summary Banner ─── -->
  <div class="summary-panel">
    <div class="summary-col">
      <div class="summary-row">
        <span class="summary-label">Review ID</span>
        <span class="summary-value summary-value--strong">{{ reviewSummary.reviewId }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Review Name</span>
        <span class="summary-value">{{ reviewSummary.reviewName }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Client Name</span>
        <span class="summary-value">{{ reviewSummary.clientName }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Market</span>
        <span class="summary-value">{{ reviewSummary.market }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Market Unit</span>
        <span class="summary-value">{{ reviewSummary.marketUnit }}</span>
      </div>
    </div>

    <div class="summary-col">
      <div class="summary-row">
        <span class="summary-label">Location to be Reviewed</span>
        <span class="summary-value">{{ reviewSummary.locationToBeReviewed }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Country</span>
        <span class="summary-value">{{ reviewSummary.country }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Proposed Month</span>
        <span class="summary-value">{{ reviewSummary.proposedMonth }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Plan Fieldwork Start Date</span>
        <span class="summary-value">{{ reviewSummary.ScheduleFieldworkStartDate | date:'dd-MMM-yyyy'}}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Plan Fieldwork End Date</span>
        <span class="summary-value">{{ reviewSummary.ScheduleFieldworkEndDate | date:'dd-MMM-yyyy'}}</span>
      </div>
    </div>

    <div class="summary-col">
      <div class="summary-row">
        <span class="summary-label">R&amp;C Reviewer</span>
        <span class="summary-value">{{ reviewSummary.rcReviewer }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">R&amp;C Supervisor</span>
        <span class="summary-value">{{ reviewSummary.rcSupervisor }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">R&amp;C Manager</span>
        <span class="summary-value">{{ reviewSummary.rcManager }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Status of Review</span>
        <span class="summary-value summary-value--strong">{{ reviewSummary.statusOfReview }}</span>
      </div>
      <div class="summary-row">
        <span class="summary-label">Single/Multiple Location</span>
        <span class="summary-value">{{ reviewSummary.singleMultipleLocation }}</span>
      </div>
    </div>
  </div>

  <!-- ─── Action Plan Form Card ─── -->
  <div class="ap-card">

    <p class="ap-id-heading">Action Plan ID: <span class="ap-id-value">AP-{{ actionPlanId }}</span></p>

    <!-- Row 1: Description (RO) | Closed Date (RO) | Internal Dependency -->
    <div class="form-grid">
      <div class="field-group">
        <label class="field-label">Action Plan Description</label>
        @if (isLinkedIssueDraftOrRejected) {
          <textarea class="field-editable field-editable-textarea" [(ngModel)]="actionPlan.description"
            placeholder="Enter action plan description"></textarea>
        } @else {
          <div class="field-input field-textarea">{{ actionPlan.description }}</div>
        }
      </div>
      <div class="field-group">
        <label class="field-label">Closed Date for Action Plan @if (isClosedDateRequired && isClosedDateEditable) { <span class="required">*</span> }</label>
        @if (isClosedDateEditable) {
          <div class="date-picker-wrapper" (click)="openDatePicker(closedDatePicker)">
            <span class="date-display-text">{{ formatDateDisplay(actionPlan.closedDate) }}</span>
            <span class="date-picker-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555555" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
            <input #closedDatePicker type="date" class="date-input-hidden"
              [(ngModel)]="actionPlan.closedDate" (ngModelChange)="onClosedDateChange()" />
          </div>
          @if (closedDateError) {
            <span class="field-error">Closed Date for the action plan is required when status is Closed.</span>
          }
        } @else {
          <div class="field-input">{{ formatDateDisplay(actionPlan.closedDate) }}</div>
        }
      </div>
      <div class="field-group">
        <label class="field-label">Internal Dependency</label>
        @if (!viewOnly) {
          <div class="ave-select-wrapper">
            <input type="text" class="field-editable ave-dep-search" placeholder="Search"
              [(ngModel)]="internalDependencySearch"
              (focus)="internalDependencyDropdownOpen = true"
              (input)="internalDependencyDropdownOpen = true"
              (blur)="closeInternalDependencyDropdown()" />
            <span class="ave-select-search-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#555" stroke-width="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
            </span>
            @if (internalDependencyDropdownOpen && getFilteredInternalDependencyOptions().length) {
              <ul class="ave-multiselect-dropdown">
                @for (opt of getFilteredInternalDependencyOptions(); track opt) {
                  <li class="ave-multiselect-option" (mousedown)="selectInternalDependency(opt)">{{ opt }}</li>
                }
              </ul>
            }
          </div>
          @if (actionPlan.internalDependencies.length) {
            <div class="ave-subscriber-chips">
              @for (dep of actionPlan.internalDependencies; track dep) {
                <span class="ave-chip">{{ dep }}<span class="ave-chip-remove" (click)="removeInternalDependency(dep)">×</span></span>
              }
            </div>
          }
        } @else {
          <div class="field-input">{{ actionPlan.internalDependencies.join(', ') }}</div>
        }
      </div>
    </div>

    <!-- Row 2: Target Date (RO) | Owner | Action Plan Status -->
    <div class="form-grid">
      <div class="field-group">
        <label class="field-label">Target Date for Action Plan/Remediation Action @if (isTargetDateMandatory) { <span class="required">*</span> }</label>
        @if (isLinkedIssueDraftOrRejected) {
          <div class="date-picker-wrapper" (click)="openDatePicker(targetDateApPicker)">
            <span class="date-display-text">{{ formatDateDisplay(actionPlan.targetDate) }}</span>
            <span class="date-picker-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555555" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
            </span>
            <input #targetDateApPicker type="date" class="date-input-hidden"
              [(ngModel)]="actionPlan.targetDate" (ngModelChange)="targetDateError = false" />
          </div>
          @if (targetDateError) {
            <span class="field-error">Target date for the action plan is required when status is Open or Open-Ageing &amp; Static.</span>
          }
        } @else {
          <div class="field-input">{{ formatDateDisplay(actionPlan.targetDate) }}</div>
        }
      </div>
      <div class="field-group">
        <label class="field-label">Owner for Action Plan/Remediation Action</label>
        @if (!viewOnly) {
          <div class="owner-picker-wrap">
            <app-people-picker
              [(ngModel)]="ownerPickerValue"
              placeholder="Search"
              (personSelected)="onOwnerPersonSelected($event)">
            </app-people-picker>
          </div>
          <div class="tags-row">
            @for (owner of actionPlan.owners; track owner) {
              <span class="tag">{{ owner }}<span class="tag-x tag-x--btn" (click)="removeOwner(owner)">&#x2715;</span></span>
            }
          </div>
        } @else {
          <div class="field-input field-placeholder">Search</div>
          <div class="tags-row">
            @for (owner of actionPlan.owners; track owner) {
              <span class="tag">{{ owner }}<span class="tag-x tag-x--disabled">&#x2715;</span></span>
            }
          </div>
        }
      </div>
      <div class="field-group" #statusField>
        <label class="field-label">Action Plan Status</label>
        @if (!viewOnly) {
          <div class="custom-select status-select" [class.custom-select--open]="statusDropdownOpen" [class.custom-select--error]="statusError">
            <div class="field-editable custom-select__trigger" (click)="toggleStatusDropdown()">
              <span class="custom-select__text">{{ actionPlan.actionPlanStatus || 'Select' }}</span>
              <span class="custom-select__chevron">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#333" stroke-width="2.5">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </span>
            </div>
            @if (statusDropdownOpen) {
              <ul class="custom-select__list">
                @for (opt of statusOptions; track opt) {
                  <li class="custom-select__option"
                    [class.custom-select__option--selected]="actionPlan.actionPlanStatus === opt"
                    (mousedown)="$event.preventDefault(); selectStatus(opt)">
                    {{ opt }}
                  </li>
                }
              </ul>
            }
          </div>
          @if (statusError) {
            <span class="field-error">Action Plan Status is required.</span>
          }
        } @else {
          <div class="field-input">{{ actionPlan.actionPlanStatus }}</div>
        }
      </div>
    </div>

    <!-- Row 3: Revised Target Date | R&C Comments | Upload File -->
    <div class="form-grid">
      <div class="field-group">
        <label class="field-label">Revised Target Date</label>
        @if (!viewOnly) {
          <div class="date-picker-wrapper" (click)="openDatePicker(revisedDatePicker)">
            <span class="date-display-text">{{ formatDateDisplay(actionPlan.revisedTargetDate) }}</span>
            <span class="date-picker-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555555" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="16" y1="2" x2="16" y2="6" />
              </svg>
            </span>
            <input #revisedDatePicker type="date" class="date-input-hidden"
              [(ngModel)]="actionPlan.revisedTargetDate" />
          </div>
        } @else {
          <div class="field-input">{{ formatDateDisplay(actionPlan.revisedTargetDate) }}</div>
        }
      </div>
      <div class="field-group">
        <label class="field-label">{{ isGoLive ? 'R&C Comments for Action Plan' : 'R&C Comments' }}</label>
        @if (!viewOnly) {
          <textarea class="field-editable field-editable-textarea" [(ngModel)]="actionPlan.rcComments"
            placeholder="Enter R&C comments"></textarea>
        } @else {
          <div class="field-input field-textarea">{{ actionPlan.rcComments }}</div>
        }
      </div>
      <div class="field-group">
        <label class="field-label">Upload File</label>
        @if (!viewOnly) {
          <input #fileInput type="file" multiple
            accept=".pdf,.msg,.xls,.xlsx,.doc,.docx,.txt,.ppt,.pptx,.jpg,.jpeg,.png,.zip"
            (change)="onFileSelected($event)" class="hidden-file-input" />
          @if (fileError) {
            <p class="file-error-msg">{{ fileError }}</p>
          }
          @if (actionPlan.uploadedDocuments.length > 0 || filesToAdd.length > 0) {
            <div class="upload-doc-area">
              <div class="upload-doc-trigger upload-doc-trigger--active" (click)="triggerFileInput()">
                <span class="material-symbols-outlined upload-doc-icon">attach_file</span>
                <span class="upload-doc-label">Attach Document</span>
              </div>
              @for (doc of actionPlan.uploadedDocuments; track doc.documentId) {
                <div class="upload-doc-item">
                  <span class="doc-filename doc-filename--link" (click)="downloadDocument(doc)">{{ doc.fileName }}</span>
                  <span class="material-symbols-outlined doc-delete-icon doc-delete-icon--active"
                    (click)="deleteDocument(doc)">delete</span>
                </div>
              }
              @for (file of filesToAdd; track $index) {
                <div class="upload-doc-item">
                  <span class="doc-filename doc-filename--pending">{{ file.name }}</span>
                  <span class="material-symbols-outlined doc-delete-icon doc-delete-icon--active"
                    (click)="removeNewFile($index)">delete</span>
                </div>
              }
            </div>
          } @else {
            <div class="field-upload upload-doc-trigger--active" (click)="triggerFileInput()">
              <span class="material-symbols-outlined upload-icon">attach_file</span>
              <span class="upload-text">Upload Document</span>
            </div>
          }
        } @else {
          @if (actionPlan.uploadedDocuments.length > 0) {
            <div class="upload-doc-area">
              <div class="upload-doc-trigger">
                <span class="material-symbols-outlined upload-doc-icon">attach_file</span>
                <span class="upload-doc-label">Attach Document</span>
              </div>
              @for (doc of actionPlan.uploadedDocuments; track doc.documentId) {
                <div class="upload-doc-item">
                  <span class="doc-filename doc-filename--link" (click)="downloadDocument(doc)">{{ doc.fileName }}</span>
                </div>
              }
            </div>
          } @else {
            <div class="field-upload">
              <span class="material-symbols-outlined upload-icon">attach_file</span>
              <span class="upload-text">Upload Document</span>
            </div>
          }
        }
      </div>
    </div>

    <!-- Row 4: Client Dependency | Red Client Date | Target Date to Inform -->
    <div class="form-grid">
      <div class="field-group">
        <label class="field-label">Client Dependency</label>
        @if (isLinkedIssueDraftOrRejected) {
          <div class="radio-group">
            <label class="radio-label">
              <input type="radio" name="clientDependency" value="yes"
                [(ngModel)]="actionPlan.clientDependency" />
              Yes
            </label>
            <label class="radio-label">
              <input type="radio" name="clientDependency" value="no"
                [(ngModel)]="actionPlan.clientDependency" />
              No
            </label>
          </div>
        } @else {
          <div class="radio-group">
            <label class="radio-label radio-label--readonly">
              <input type="radio" name="clientDependency" value="yes"
                [checked]="actionPlan.clientDependency === 'yes'" disabled />
              Yes
            </label>
            <label class="radio-label radio-label--readonly">
              <input type="radio" name="clientDependency" value="no"
                [checked]="actionPlan.clientDependency === 'no'" disabled />
              No
            </label>
          </div>
        }
      </div>
      <div class="field-group">
        <label class="field-label">{{ isGoLive ? 'Red-Client Related Observation-Client Informed Date' : 'Red Client Observation Client Informed Date' }}</label>
        @if (!viewOnly) {
          <div class="date-picker-wrapper" (click)="openDatePicker(redClientDatePicker)">
            <span class="date-display-text">{{ formatDateDisplay(actionPlan.redClientObservationDate) }}</span>
            <span class="date-picker-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555555" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="16" y1="2" x2="16" y2="6" />
              </svg>
            </span>
            <input #redClientDatePicker type="date" class="date-input-hidden"
              [(ngModel)]="actionPlan.redClientObservationDate" />
          </div>
        } @else {
          <div class="field-input">{{ formatDateDisplay(actionPlan.redClientObservationDate) }}</div>
        }
      </div>
      <div class="field-group">
        <label class="field-label">Target Date to inform Red client Observation Client informed date</label>
        @if (!viewOnly) {
          <div class="date-picker-wrapper" (click)="openDatePicker(targetDatePicker)">
            <span class="date-display-text">{{ formatDateDisplay(actionPlan.targetDateToInformClient) }}</span>
            <span class="date-picker-icon">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#555555" stroke-width="2">
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="3" y1="9" x2="21" y2="9" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="16" y1="2" x2="16" y2="6" />
              </svg>
            </span>
            <input #targetDatePicker type="date" class="date-input-hidden"
              [(ngModel)]="actionPlan.targetDateToInformClient" />
          </div>
        } @else {
          <div class="field-input">{{ formatDateDisplay(actionPlan.targetDateToInformClient) }}</div>
        }
      </div>
    </div>

    <!-- Row 5: Legacy AP Number -->
    <div class="form-grid">
      <div class="field-group">
        <label class="field-label">Legacy AP Number (AuditBoard AP ID)</label>
        @if (isLinkedIssueDraftOrRejected) {
          <input type="text" class="field-editable" [(ngModel)]="actionPlan.legacyApNumber"
            placeholder="Enter legacy AP number" />
        } @else {
          <div class="field-input">{{ actionPlan.legacyApNumber }}</div>
        }
      </div>
    </div>

    <!-- ─── Linked Issue Section (read-only) ─── -->
    <div class="linked-issue-section">

      <div class="linked-issue-title-bar">
        <h3 class="linked-issue-title">Linked Issue</h3>
      </div>

      <div class="linked-issue-body">

        <div class="linked-issue-search-row">
          <div class="linked-issue-search-wrapper">
            <input type="text" class="linked-issue-search-input" placeholder="Search Issue"
              [(ngModel)]="issueSearch" />
            <span class="linked-issue-search-icon" aria-hidden="true">
              <mat-icon>search</mat-icon>
            </span>
          </div>
        </div>

        <div class="linked-issue-table-wrapper">
          <table class="linked-issue-table">
            <thead>
              <tr>
                <th class="th-issue-id">Issue ID</th>
                <th class="th-issue-title">Issue Title</th>
                <th class="th-issue-desc">Issue Description</th>
                <th class="th-issue-rating">Issue Rating</th>
                <th class="th-issue-status">Issue Status</th>
                <th class="th-issue-location">Location Reviewed and Impacted</th>
                <th class="th-action">Action</th>
              </tr>
            </thead>
            <tbody>
              @for (issue of filteredIssues; track issue.id) {
                <tr>
                  <td class="issue-id-cell">{{ issue.id }}</td>
                  <td class="issue-title-cell" [title]="issue.title">{{ issue.title }}</td>
                  <td class="issue-desc-cell" [title]="issue.description">{{ issue.description }}</td>
                  <td class="issue-rating-cell">
                    @if (issue.rating) {
                      <span class="rating-badge" [class]="getRatingClass(issue.rating)">{{ issue.rating }}</span>
                    }
                  </td>
                  <td class="issue-status-cell">{{ issue.status }}</td>
                  <td class="issue-location-cell" [title]="issue.locationReviewed">{{ issue.locationReviewed }}</td>
                  <td>
                    <div class="issue-action-cell">
                      <input type="checkbox" [(ngModel)]="issue.linked"
                        [disabled]="!isAdmin"
                        [class.checkbox-interactive]="isAdmin"
                        (change)="onLinkedIssueChange()" />
                      <span class="issue-link-label">Link</span>
                    </div>
                  </td>
                </tr>
              }
              @if (filteredIssues.length === 0) {
                <tr>
                  <td colspan="7" class="issue-empty">No records found</td>
                </tr>
              }
            </tbody>
          </table>
        </div>

      </div>
    </div>

    <div class="form-divider"></div>

    <!-- ─── Warning Message ─── -->
    <div class="warning-bar">
      <span class="material-symbols-outlined warning-icon-box">announcement</span>
      <span class="warning-text">
        If the mandatory information (<span class="warning-link warning-link--red">fields</span>) is not available in the form,
        To request to add missing information please contact admin to get added that information via
        '<span class="warning-link warning-link--blue">Request to Add information&lt;Email Id&gt;</span>',
        meanwhile you can use '<span class="warning-link warning-link--violet">Save</span>' functionality to save your form.
        E.g. The master client, Location to Be Review , is not available for you then contract admin to get it added.
      </span>
    </div>

    <!-- ─── Form Actions ─── -->
    <div class="form-actions">
      <button type="button" class="btn-cancel" (click)="cancel()">Cancel</button>
      <button type="button" class="btn-update" (click)="update()"
        [disabled]="isUpdating || (viewOnly ? !isLinkedIssuesDirty : (!isDirty && !isLinkedIssuesDirty))">
        Update
      </button>
    </div>

  </div>

</main>

<!-- ── Success Toast ── -->
<div class="toast-success" [class.toast-visible]="showToast">
  <mat-icon class="toast-icon">check_circle</mat-icon>
  <div class="toast-body">
    <span class="toast-title">Success</span>
    <span class="toast-message">{{ toastMessage }}</span>
  </div>
  <mat-icon class="toast-close" (click)="dismissToast()">close</mat-icon>
</div>
